import type { FC } from 'react'
import { useEffect } from 'react'

import CheckboxWithDisabledTooltip from '@/components/CheckboxWithDisabledTooltip'

export interface Filter {
  essential: boolean
  priority: boolean
  pro: boolean
  corporate: boolean
}

export const DEFAULT_FILTER: Filter = {
  essential: true,
  priority: false,
  pro: false,
  corporate: false,
}

interface Props {
  filter: Filter
  onChangeFilter: (filter: Filter) => void
  onlyShowWorkingQueue: boolean
}

const ProductFilter: FC<Props> = ({ filter, onChangeFilter, onlyShowWorkingQueue }) => {
  const isNonPriorityProductSelected = filter.essential || filter.pro || filter.corporate

  useEffect(() => {
    if (onlyShowWorkingQueue && isNonPriorityProductSelected && filter.priority) {
      onChangeFilter({
        ...filter,
        priority: false,
      })
    }
  }, [
    isNonPriorityProductSelected,
    filter.priority,
    filter,
    onlyShowWorkingQueue,
    onChangeFilter,
  ])

  const onChangeNonPriorityFilter: (
    key: string,
  ) => React.ChangeEventHandler<HTMLInputElement> = key => e =>
    onlyShowWorkingQueue
      ? onChangeFilter({
          ...filter,
          [key]: e.target.checked,
          priority: false,
        })
      : onChangeFilter({
          ...filter,
          [key]: e.target.checked,
        })

  const onChangePriorityFilter: React.ChangeEventHandler<HTMLInputElement> = e =>
    onlyShowWorkingQueue
      ? onChangeFilter({
          ...filter,
          essential: false,
          priority: e.target.checked,
          corporate: false,
        })
      : onChangeFilter({
          ...filter,
          priority: e.target.checked,
        })

  return (
    <>
      <CheckboxWithDisabledTooltip
        label="Essential"
        checked={filter.essential}
        onChange={onChangeNonPriorityFilter('essential')}
        disabled={filter.priority && onlyShowWorkingQueue}
        disabledTooltip="Cannot select Essential tier while Priority tier is selected and 'Just show todays queue' is enabled."
      />
      <CheckboxWithDisabledTooltip
        label="Pro"
        checked={filter.pro}
        onChange={onChangeNonPriorityFilter('pro')}
        disabled={filter.priority && onlyShowWorkingQueue}
        disabledTooltip="Cannot select Pro tier while Priority tier is selected and 'Just show todays queue' is enabled."
      />
      <CheckboxWithDisabledTooltip
        label="Priority"
        checked={filter.priority}
        onChange={onChangePriorityFilter}
        disabled={isNonPriorityProductSelected && onlyShowWorkingQueue}
        disabledTooltip="Cannot select Priority tier while other tiers are also selected and 'Just show todays queue' is enabled."
      />
      <CheckboxWithDisabledTooltip
        label="Corporate Let"
        checked={filter.corporate}
        onChange={onChangeNonPriorityFilter('corporate')}
        disabled={filter.priority && onlyShowWorkingQueue}
        disabledTooltip="Cannot select Corporate Let tier while Priority tier is selected and 'Just show todays queue' is enabled."
      />
    </>
  )
}

export default ProductFilter
